// apps/client/src/hooks/useLoanDetails.ts
import { useState, useEffect } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../lib/firebase';

interface PaymentEvent {
  paymentType: string;
  commissionCents: number;
  settlementDate: string;
  invoicedDate: string;
  brokerName: string;
  lender: string;
  ingestionTimestamp: string;
  runNumber: number;
  runDate: string;
  comments: string | null;
}

interface LoanDetailsData {
  loanId: string;
  customerName: string;
  lender: string;
  settlementDate: string;
  loanAmountCents: number;
  loanBalanceCents: number;
  paymentEvents: PaymentEvent[];
  summary: {
    grossUpfrontCents: number;
    upfrontDeductionsCents: number;
    netUpfrontCents: number;
    grossTrailCents: number;
    trailDeductionsCents: number;
    netTrailCents: number;
    dischargedCommissionCents: number;
    totalGrossCents: number;
    totalNetCents: number;
  };
}

interface UseLoanDetailsParams {
  companyId: string;
  loanId: string | null;
  officeId?: string;
  brokerId?: string;
}

interface UseLoanDetailsReturn {
  loanDetails: LoanDetailsData | null;
  loading: boolean;
  error: string | null;
  refetch: () => void;
}

/**
 * Hook to fetch loan detail breakdown from BigQuery
 *
 * Shows all payment events for a specific loan (Upfront, Trail, Deductions)
 * to understand gross vs net commission breakdown.
 *
 * RBAC: Automatically filters based on user role
 * - broker: See only their own loans
 * - office_admin: See loans in their office
 * - administrator/super_admin: See all company loans
 */
export function useLoanDetails({
  companyId,
  loanId,
  officeId,
  brokerId,
}: UseLoanDetailsParams): UseLoanDetailsReturn {
  const [loanDetails, setLoanDetails] = useState<LoanDetailsData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchLoanDetails = async () => {
    if (!companyId || !loanId) {
      setLoanDetails(null);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      console.log(`[useLoanDetails] Calling BigQuery getLoanDetails:`, {
        companyId,
        loanId,
        brokerId: brokerId || 'all',
        officeId: officeId || 'all',
      });

      const getLoanDetailsFunc = httpsCallable(functions, 'getLoanDetails');
      const result = await getLoanDetailsFunc({
        companyId,
        loanId,
        ...(officeId && { officeId }),
        ...(brokerId && { brokerId }),
      });

      const data = result.data as LoanDetailsData;

      console.log(`[useLoanDetails] Received ${data.paymentEvents.length} payment events`);
      console.log(`[useLoanDetails] Summary:`, data.summary);

      setLoanDetails(data);
      setError(null);
    } catch (err: any) {
      console.error('[useLoanDetails] Error fetching loan details:', err);
      setError(err.message || 'Failed to fetch loan details');
      setLoanDetails(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLoanDetails();
  }, [companyId, loanId, officeId, brokerId]);

  return {
    loanDetails,
    loading,
    error,
    refetch: fetchLoanDetails,
  };
}
